import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceEmailCsv,
  UserImportDataSourceStatus,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import {
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DataGridVertical } from '../../../../../../components/DataGrid';
import { DateFormatPicker } from '../../../../../../components/DateFormatPicker';
import { InfoBox } from '../../../../../../components/InfoBox';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc, updateDoc } from '../../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import { CustomImportMultiMappingForm2 } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.EMAIL_CSV;

const createEmailCsvSource = async (
  orgId: string,
  sourceName: string,
  allowedSenders: string[],
  createdBy: string
) => {
  const { sourceId, emailAddress, name } = await callFirebaseFunction<{
    sourceId: string;
    emailAddress: string;
    name: string;
  }>('emailCsvImport_createSource', {
    orgId,
    name: sourceName,
    allowedSenders
  });

  const doc: Doc<UserImportDataSourceEmailCsv> = {
    id: sourceId,
    collection: FS.userImportDataSources,
    data: {
      orgId,
      type: UserImportSourceType.EMAIL_CSV,
      status: UserImportDataSourceStatus.OK,
      detailedStatus: '',
      name,
      emailAddress,
      allowedSenders,
      dateFormats: [],
      lastReceivedAt: null,
      lastSenderEmail: null,
      createdBy,
      createdAt: now(),
      lastUpdate: null,
      customMappings: []
    }
  };

  await setDoc(doc);
  return doc;
};

const ConfigureImportSource = ({
  d,
  onClose,
  customFields
}: {
  d: Doc<UserImportDataSourceEmailCsv>;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);
  const [isDirty, setIsDirty] = useState(false);

  const [state, setState] = useState<
    Pick<
      UserImportDataSourceEmailCsv,
      'customMappings' | 'inferredOtherUsers' | 'dateFormats'
    >
  >({
    customMappings: d.data.customMappings || [],
    inferredOtherUsers: d.data.inferredOtherUsers || [],
    dateFormats: d.data.dateFormats || []
  });

  return (
    <>
      <DialogContent>
        <FlexContainerVertical spacing={2}>
          <EmailCsvInfoBox d={d} />
          <DateFormatPicker
            value={state.dateFormats}
            onChange={(formats) => {
              setIsDirty(true);
              setState((x) => ({ ...x, dateFormats: formats }));
            }}
            subLabel={t(
              'Select the date formats that your CSV file may contain. If none selected, the organization default will be used.'
            )}
          />
          <CustomImportMultiMappingForm2
            additionalCompositeFields={['$FULL_NAME$']}
            enableSave={isDirty}
            fieldTerm={t('column')}
            integrationName={t('your CSV file')}
            userFields={userFields}
            value={{
              main: state.customMappings,
              others: state.inferredOtherUsers?.map((v) => v.customMappings)
            }}
            onChange={(nextVal) => {
              setIsDirty(true);
              setState((x) => ({
                ...x,
                customMappings: nextVal.main,
                inferredOtherUsers:
                  nextVal.others?.map((o) => ({
                    customMappings: o
                  })) || []
              }));
            }}
          />
        </FlexContainerVertical>
      </DialogContent>
      <DialogActions>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          pending={t('Saving')}
          disabled={!isDirty}
          onClick={async () => {
            await updateDoc(d, () => ({
              customMappings: state.customMappings,
              inferredOtherUsers: state.inferredOtherUsers,
              dateFormats: state.dateFormats,
              lastUpdate: now()
            }));
            onClose();
          }}
        >
          {t('Save')}
        </ButtonWithPromise>
      </DialogActions>
    </>
  );
};

export const EmailCsvInfoBox = ({
  d
}: {
  d: Doc<UserImportDataSourceEmailCsv>;
}) => {
  const { t } = useUnsafeTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(d.data.emailAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <InfoBox color="yellow" fullWidth>
      <DataGridVertical
        ds={[
          {
            label: t('Email Address'),
            d: (
              <FlexContainer spacing={1}>
                <span>{d.data.emailAddress}</span>
                <Tooltip title={copied ? (t('Copied!') as string) : (t('Copy') as string)}>
                  <IconButton size="small" onClick={handleCopy}>
                    <Copy size={14} />
                  </IconButton>
                </Tooltip>
              </FlexContainer>
            )
          },
          {
            label: t('Allowed Senders'),
            d: d.data.allowedSenders.join(', ') || '-'
          },
          {
            label: t('Last Received'),
            d: d.data.lastReceivedAt
              ? `${d.data.lastReceivedAt.toDate().toLocaleString()} from ${d.data.lastSenderEmail || 'unknown'}`
              : t('No files received yet')
          }
        ]}
      />
    </InfoBox>
  );
};

export const EmailCsvSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const { id: userId } = useCurrentUser();

  const [name, setName] = useState('');
  const [allowedSendersText, setAllowedSendersText] = useState('');

  const [
    importSource,
    setImportSource
  ] = useState<Doc<UserImportDataSourceEmailCsv> | null>(null);

  if (importSource) {
    return (
      <ConfigureImportSource
        d={importSource}
        onClose={onClose}
        customFields={customFields}
      />
    );
  }

  return (
    <DialogContent>
      <FlexContainerVertical spacing={2}>
        <Img maxHeight={128} src={META.logoUrl} />
        <CustomInputLabel
          label={t('Source Name')}
          subLabel={t('To be shown on the Dado Import Sources page')}
          fullWidth
        >
          <TextField
            placeholder={t('e.g. Employee Data from HRIS')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
          />
        </CustomInputLabel>

        <CustomInputLabel
          label={t('Allowed Sender Emails')}
          subLabel={t(
            'Comma-separated list of email addresses or domains (e.g. hr@acme.com, *@acme.com)'
          )}
          fullWidth
        >
          <TextField
            placeholder={t('e.g. hr@acme.com, *@acme.com')}
            value={allowedSendersText}
            onChange={(e) => setAllowedSendersText(e.target.value)}
            fullWidth
          />
        </CustomInputLabel>

        <InfoBox color="blue" fullWidth>
          <FlexContainerVertical spacing={1} fullWidth>
            <Typography variant="body1">
              {t(
                'Dado can import data from CSV files sent via email. Configure your HR system to email CSV exports to the unique address generated in the next step.'
              )}
            </Typography>
            <Typography variant="body1">
              {t(
                'Only emails from the allowed senders above will be accepted. You can update this list later.'
              )}
            </Typography>
          </FlexContainerVertical>
        </InfoBox>

        <FlexContainer justifyContent="flex-end" fullWidth>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <ButtonWithPromise
            variant="contained"
            color="primary"
            pending={t('Creating Source')}
            disabled={!name || !allowedSendersText.trim()}
            onClick={async () => {
              const allowedSenders = allowedSendersText
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
              const nextImportSource = await createEmailCsvSource(
                orgId,
                name,
                allowedSenders,
                userId
              );
              setImportSource(nextImportSource);
            }}
          >
            {t('Setup Email CSV')}
          </ButtonWithPromise>
        </FlexContainer>
      </FlexContainerVertical>
    </DialogContent>
  );
};

export const ImportSourceRowEmailCsv = ({
  d
}: {
  d: Doc<UserImportDataSourceEmailCsv>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
